// Process Setting
import { getProcessSettingApiUrl } from '../config/env';
import { FormData } from '../hooks/useFormValidation';
import logger from '../utils/logger';

export interface ProcessSettingResponse {
  ServerID: string;
  ServerIDSign: string;
  SecureSiteSettingData: string;
  SecureSiteSettingDataSign: string;
  Settings: {
    UseFedRamp: string;
  };
}

export interface ProcessSettingResult {
  success: boolean;
  data?: ProcessSettingResponse;
  error?: {
    code: string;
    message: string;
  };
}

class ProcessSetting {
  private processSettingEndpoint: string;

  /**
   * @brief Constructor to initialize the process setting class
   */
  constructor() {
    this.processSettingEndpoint = getProcessSettingApiUrl();
  }

  /**
   * @brief Generate configuration settings using JWT token
   * @param token JWT token for authorization
   * @param formData Form data containing configuration details
   * @return Configuration result object if successful, error object otherwise
   */
  public async generateConfiguration(
    token: string,
    formData: FormData
  ): Promise<ProcessSettingResult> {
    try {
      // Validate input token
      if (!token || typeof token !== 'string') {
        logger.error('Invalid token provided to generateConfiguration');
        return {
          success: false,
          error: {
            code: 'INVALID_TOKEN',
            message: 'Invalid token provided'
          }
        };
      }

      // Validate endpoint configuration
      if (!this.processSettingEndpoint) {
        logger.error('REACT_APP_PROCESS_SETTING_ENDPOINT not configured');
        return {
          success: false,
          error: {
            code: 'ENDPOINT_NOT_CONFIGURED',
            message: 'Process setting endpoint not configured'
          }
        };
      }

      // Compute settingsValue bitmask from selected options
      const flags = formData.settingsValue;
      let settingsBitmask = 0;
      if (flags.disableProductUsage) settingsBitmask |= 1; // Prevent_Data_Collection
      if (flags.disconnected) settingsBitmask |= 2; // Prevent_Internet_Connected_Components
      if (flags.disableServiceSystem) settingsBitmask |= 4; // Prevent_Service_System_Context
      if (flags.readOnly) settingsBitmask |= 8; // Prevent_Uploads
      if (flags.disableGenuineCompliance) settingsBitmask |= 16; // Disable_Compliance_Data_Collection
      if (flags.disableLicensingCompliance) settingsBitmask |= 32; // Disable_Licensing_Compliance_Data_Collection
      if (flags.fedRamp) settingsBitmask |= 512; // Setup_FedRamp

      const payload = {
        ...formData,
        settingsValue: String(settingsBitmask)
      };

      // Prepare request headers
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Make POST request to process setting endpoint
      const response = await fetch(this.processSettingEndpoint, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload),
        credentials: 'include'  // Required for CORS with allowCredentials: true
      });

      const result = await response.json();

      if (!response.ok) {
        logger.error('Configuration generation failed:', result);
        // Backend returns: { status: 'error', error: { code, message } }
        const errorMessage = result?.error?.message || result?.message || `Request failed with status: ${response.status}`;
        const errorCode = result?.error?.code || 'REQUEST_FAILED';
        return {
          success: false,
          error: {
            code: errorCode,
            message: errorMessage
          }
        };
      }

      // Return successful response
      return {
        success: true,
        data: result as ProcessSettingResponse
      };

    } catch (error) {
      logger.error('Error during configuration generation:', error);
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error during configuration generation'
        }
      };
    }
  }
}

export default ProcessSetting;

